import React, { useState } from "react";
import { toast } from "sonner";
import { UserRound, Loader2, Save, X } from "lucide-react";
import { webUsersApi, authApi, setCurrentUser } from "../../lib/api";
import { getApiError } from "../../lib/utils";

/** Self-serve rename modal wired from the profile menu. Refreshes /me so
 *  the sidebar/top-bar shows the new name immediately. */
export default function EditNameModal({ open, user, onClose, onRenamed }) {
  const [name, setName] = useState(user?.display_name || "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) setName(user?.display_name || "");
  }, [open, user?.display_name]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    if (trimmed.length < 2) { toast.error("Name must be at least 2 characters"); return; }
    if (trimmed === user?.display_name) { toast.info("Name unchanged"); onClose(); return; }
    setSaving(true);
    try {
      await webUsersApi.update(user.user_id, { display_name: trimmed });
      try {
        const fresh = await authApi.me();
        setCurrentUser(fresh);
      } catch (meErr) {
        console.warn("[edit-name] /me refresh failed:", meErr);
      }
      toast.success("Name updated");
      onRenamed?.();
      onClose();
    } catch (err) {
      toast.error(getApiError(err, "Rename failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-0 sm:p-4"
      data-testid="edit-name-modal"
      onClick={() => !saving && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md overflow-hidden rounded-t-xl sm:rounded-md border border-slate-200 bg-white shadow-2xl pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-start justify-between border-b border-slate-200 p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-nmi-100">
              <UserRound size={16} className="text-nmi-700" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-heading text-base font-bold text-slate-900">Edit your name</h3>
              <p className="text-[11px] text-slate-500">This is how you'll appear on audit logs and to your team.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 sm:p-5 space-y-3">
          <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            Display name
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            disabled={saving}
            data-testid="edit-name-input"
            placeholder="e.g. Jane Doe"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-3 sm:py-2.5 text-base sm:text-sm outline-none focus:border-nmi-600 focus:ring-1 focus:ring-nmi-600"
          />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-name-save"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-nmi-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-nmi-800 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={2.5} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
